<template>
  <div v-if="isActualDirector" class="ml-2">
    <v-menu
      v-model="menu"
      offset-y
      :close-on-content-click="true"
      @input="onMenuToggle"
      transition="slide-y-transition"
      origin="top right"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-chip
          v-bind="attrs"
          v-on="on"
          :class="['view-as-role-chip white--text px-1', { 'view-as-role-chip--active': isViewingAsRole }]"
          small
          :title="chipTooltip"
        >
          <v-icon small>mdi-eye</v-icon>
        </v-chip>
      </template>

      <v-card class="view-as-role-menu" min-width="220" max-width="240" rounded="lg" elevation="8">
        <div class="view-as-role-menu__header pa-2">
          <div class="d-flex align-center">
            <v-avatar size="28" class="mr-2 view-as-role-menu__header-avatar">
              <v-icon x-small color="white">mdi-eye-outline</v-icon>
            </v-avatar>
            <div class="text-body-2 font-weight-bold view-as-role-menu__header-title line-height-tight">
              Ver como cargo
            </div>
          </div>
        </div>

        <v-divider class="my-0"></v-divider>

        <v-list class="py-1 px-1" dense>
          <v-list-item
            class="view-as-role-item rounded mb-0"
            :class="{ 'view-as-role-item--active': !isViewingAsRole }"
            @click="selectRole(null)"
          >
            <v-list-item-avatar class="my-0 mr-2" size="28" min-width="28">
              <v-avatar
                size="28"
                :class="['view-as-role-item__avatar', { 'view-as-role-item__avatar--active': !isViewingAsRole }]"
              >
                <v-icon x-small :color="!isViewingAsRole ? 'white' : 'blue-grey darken-2'">
                  mdi-crown-outline
                </v-icon>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content class="py-1">
              <v-list-item-title class="body-2 font-weight-medium">Meu cargo</v-list-item-title>
              <v-list-item-subtitle class="caption">Diretor</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="!isViewingAsRole" class="my-0">
              <v-icon x-small class="view-as-role-brand-icon">mdi-check-circle</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <template v-if="roles.length">
          <v-divider class="my-0"></v-divider>
          <div class="view-as-role-menu__scroll px-1 pb-1">
            <div class="view-as-role-menu__section-label caption grey--text px-2 py-1">
              Outros cargos
            </div>
            <v-list dense class="py-0">
              <v-list-item
                v-for="role in roles"
                :key="role.id"
                class="view-as-role-item rounded mb-0"
                :class="{ 'view-as-role-item--active': isRoleSelected(role) }"
                @click="selectRole(role)"
              >
                <v-list-item-avatar class="my-0 mr-2" size="28" min-width="28">
                  <v-avatar
                    size="28"
                    :class="['view-as-role-item__avatar', { 'view-as-role-item__avatar--active': isRoleSelected(role) }]"
                  >
                    <v-icon
                      x-small
                      :color="isRoleSelected(role) ? 'white' : 'blue-grey darken-2'"
                    >
                      {{ roleIcon(role.attributes.name) }}
                    </v-icon>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content class="py-1">
                  <v-list-item-title class="body-2 font-weight-medium">
                    {{ roleLabel(role) }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="isRoleSelected(role)" class="my-0">
                  <v-icon x-small class="view-as-role-brand-icon">mdi-check-circle</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </template>

        <v-list v-else dense class="py-1 px-1">
          <v-list-item disabled class="view-as-role-item">
            <v-list-item-content class="py-1">
              <v-list-item-subtitle class="text-center caption">
                Carregando...
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Api from '@/api/index'

const ROLE_ICONS = {
  Seller: 'mdi-account-tie-outline',
  Manager: 'mdi-account-star-outline',
  SalesExecutive: 'mdi-briefcase-account-outline',
  Financial: 'mdi-cash-multiple',
  Logistics: 'mdi-truck-outline',
  CreditAnalyst: 'mdi-chart-line-variant',
  Analyst: 'mdi-magnify-scan',
  Assistant: 'mdi-headset',
  Driver: 'mdi-steering',
}

export default {
  name: 'ViewAsRoleChip',
  data() {
    return {
      menu: false,
      roles: [],
    }
  },
  computed: {
    ...mapState('App', ['viewAsRoleName', 'viewAsRoleTranslated']),
    isActualDirector() {
      return this.isDirectorRoleName(this.actualUserRoleName)
    },
    isViewingAsRole() {
      return !!this.viewAsRoleName
    },
    actualUserRoleName() {
      return this.$store?.state?.User?.oneUser?.included?.[0]?.attributes?.role?.data?.[0]?.attributes?.name
    },
    chipTooltip() {
      if (this.isViewingAsRole) {
        return `Visualizando como: ${this.viewAsRoleTranslated || this.viewAsRoleName}`
      }
      return 'Ver sistema como outro cargo'
    },
  },
  methods: {
    ...mapActions('App', ['setViewAsRole']),
    isDirectorRoleName(name) {
      if (!name) return false
      const normalizedName = String(name).trim().toLowerCase()
      return normalizedName === 'director' || normalizedName === 'diretor'
    },
    isRoleSelected(role) {
      return this.viewAsRoleName === role?.attributes?.name
    },
    roleLabel(role) {
      return role?.attributes?.translated || role?.attributes?.name
    },
    roleIcon(roleName) {
      return ROLE_ICONS[roleName] || 'mdi-account-circle-outline'
    },
    parseRolesPayload(payload) {
      if (!payload) return []
      if (Array.isArray(payload.data)) return payload.data
      if (typeof payload === 'string') {
        try {
          return this.parseRolesPayload(JSON.parse(payload))
        } catch (_e) {
          return []
        }
      }
      return []
    },
    loadRoles() {
      Api.EmployeeRole.index()
        .then(r => r.data)
        .then(data => {
          this.roles = this.parseRolesPayload(data).filter(
            r => !this.isDirectorRoleName(r?.attributes?.name)
          )
        })
        .catch(err => {
          console.error('[ViewAsRoleChip] Falha ao carregar cargos', err)
        })
    },
    onMenuToggle(open) {
      if (open && this.isActualDirector) this.loadRoles()
    },
    selectRole(role) {
      const name = role ? role.attributes.name : null
      const translated = role ? (role.attributes.translated || role.attributes.name) : null
      this.setViewAsRole({ name, translated })
      this.menu = false
      if (this.$route.path !== '/') {
        this.$router.push({ name: 'Home' }).catch(() => {})
      } else {
        window.location.reload()
      }
    },
  },
  mounted() {
    if (this.isActualDirector) this.loadRoles()
  },
  watch: {
    isActualDirector: {
      immediate: true,
      handler(isDirector) {
        if (isDirector) this.loadRoles()
      },
    },
    actualUserRoleName() {
      if (this.isActualDirector) this.loadRoles()
    },
  },
}
</script>

<style scoped>
.view-as-role-menu {
  --view-as-brand: #ed1c24;
  --view-as-g1: #607d8b;
  --view-as-g2: #546e7a;
  --view-as-g3: #455a64;
  --view-as-g4: #37474f;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.view-as-role-chip {
  background-color: var(--view-as-g2) !important;
}

.view-as-role-chip--active {
  background-color: var(--view-as-brand) !important;
}

.view-as-role-menu__header {
  background: linear-gradient(
    125deg,
    var(--view-as-g1) 0%,
    var(--view-as-g2) 38%,
    var(--view-as-g3) 78%,
    var(--view-as-g4) 100%
  );
  border-bottom: 3px solid var(--view-as-brand);
  box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.07);
}

.view-as-role-menu__header-title {
  color: #fff;
}

.view-as-role-menu__header-avatar {
  background: rgba(255, 255, 255, 0.15) !important;
}

.line-height-tight {
  line-height: 1.2;
}

.view-as-role-menu__scroll {
  max-height: 180px;
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
}

.view-as-role-menu__scroll::-webkit-scrollbar {
  width: 4px;
}

.view-as-role-menu__scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.view-as-role-menu__section-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
}

.view-as-role-brand-icon {
  color: var(--view-as-brand) !important;
}

.view-as-role-item__avatar {
  background: #eceff1 !important;
}

.view-as-role-item__avatar--active {
  background: linear-gradient(
    125deg,
    var(--view-as-g2) 0%,
    var(--view-as-g3) 100%
  ) !important;
}

.view-as-role-item {
  min-height: 36px !important;
  transition: background-color 0.15s ease;
}

.view-as-role-item:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.view-as-role-item--active {
  background-color: #fafafa !important;
  box-shadow: inset 3px 0 0 var(--view-as-brand);
}
</style>
